/**
 * Turns Create Schedule form state into the body the Witmeg report scheduler
 * expects:
 *
 *   POST {REACT_APP_SCHEDULER_BASE_URL}/v1/scheduler/create
 *
 * Pure on purpose — the payload can be built and inspected without a network
 * call or a rendered page.
 *
 * ── Form fields the payload has nowhere to put ──────────────────────────────
 * Backend asked us to leave these out rather than invent slots, so their
 * controls are disabled on the page and they belong to our own schedule record
 * if we add one later:
 *
 *   publisher        no publisher field exists; CreatedBy is the creator
 *   status "Draft"   payload only has IsActive, which is not the same thing
 *   recipient names  EmailID is a flat array of addresses
 *   phone numbers    PhoneNumbers exists, but Send To collects email only
 *
 * ── Recurrence ──────────────────────────────────────────────────────────────
 * The page's Repeat field has two options, so only two blocks are ever sent:
 * NotRepeat, or Custom. The scheduler's Daily / Weekly / Monthly / Annually
 * blocks are left alone — Custom.RecurrenceType carries that choice instead,
 * and Custom.Ends carries Range of Recurrence.
 *
 * Only the active block needs sending; the others come back empty and nothing
 * complains. Verified against the dev service on 2026-09-01.
 *
 * One thing still bent out of shape:
 *   daily "every weekday" -> RecurrenceType Weekly with Mon-Fri, interval 1.
 *                            Says the same thing, just not in the same words.
 */

import { WEEKDAYS, labelOf } from "./RecurrencePatternsForms/options";

/* Per the API doc (17-07-2023): 1 = ends on a date, 2 = ends after a number of
   executions, 3 = no end. There is no 0 — an earlier guess used one.

   These are kept alongside Custom.Ends, which says the same thing in the newer
   shape, because ValidTo and ScheduleEndCondition are marked Required. */
const END_CONDITION = { endBy: 1, after: 2, none: 3 };

/* The scheduler's own wording for the Status field it added so draft and published
   schedules can live in one table. Named here rather than inlined so the list page reads
   the same strings back - see scheduleMappers.js.

   ── LEADING CAPITAL FROM 2026-09-17 ──
   Sent as "Published" rather than "published", as asked for. Reading is unaffected
   either way: statusOf lowercases before comparing, so records saved under the old
   spelling still show the right pill. Previous values kept for a quick revert. */
export const STATUS = { draft: "Draft", published: "Published" };

// export const STATUS = { draft: "draft", published: "published" };  <- before 2026-09-17

/* ── WHERE THE SCHEDULER SHOULD CALL ──────────────────────────────────────────
   Our own reporting backend, which renders the file. Given by the scheduler team on
   2026-09-11 for the development environment.

   From the environment where set, so UAT and production can differ without a code
   change; the literal is the development value and a fallback, because these files are
   gitignored now and a checkout without one would otherwise send an empty BaseURL.

   The route is the endpoint added for this - API/controllers/reportFileController.js. */
export const REPORT_API_BASE_URL =
  process.env.REACT_APP_REPORT_API_BASE_URL || "https://api-reportv1.throbox.com";
export const REPORT_API_ROUTE = "/generate-report";

/* The scheduler identifies the service it is calling by name, and it is now calling
   ours. A fixed string, given by the scheduler team - not the report's application name,
   which is what this used to send. */
export const REPORT_SERVICE_NAME = "WitmegReport";

/**
 * The application ids a board was built for.
 *
 * CreateDashboard writes appList inside its configData object, but a board is stored
 * with appList as a top-level field of its own - the dashboard schema lists it beside
 * configDetails, not within it. Reading only configDetails.appList found nothing on
 * every saved board, and did so quietly: ApplicationID and ServiceName went out empty
 * on every schedule, and the report list came back empty for every application.
 *
 * Both places are checked, so it works whichever way a given board was written.
 */
export const appListOf = (board) => {
  const top = board?.appList;
  if (Array.isArray(top) && top.length) return top;

  const nested = board?.configDetails?.appList;
  return Array.isArray(nested) ? nested : [];
};

const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

/* "01" + "00" + "PM" -> "13:00". RunAt is 24h. */
function to24Hour(time) {
  const hour = Number(time?.hour ?? 12) % 12;
  const minute = time?.minute ?? "00";
  const shifted = time?.meridiem === "PM" ? hour + 12 : hour;
  return `${String(shifted).padStart(2, "0")}:${minute}`;
}

/* Our option values are lowercase ("first"); the payload capitalises them. */
const titleCase = (value) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : "");

/* WEEKDAYS labels are already "Monday", "Tuesday" — the exact strings the
   payload's WeekDays array uses. */
const dayName = (code) => labelOf(WEEKDAYS, code);

/* Yearly goes out as a 12-month cycle, so StartAt has to fall inside the month
   the user chose. Year comes from the schedule date so a rule set up in
   December does not quietly start in January. Day is capped at 28 to stay
   valid in February. */
function startOfTargetMonth(isoDate, monthCode, dayNo) {
  const year = Number(String(isoDate || "").slice(0, 4)) || new Date().getFullYear();
  const month = MONTH_INDEX[monthCode] ?? 0;
  const day = Math.min(Math.max(Number(dayNo) || 1, 1), 28);
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/* Monday to Friday, in the scheduler's own spelling — the days behind the
   "Every weekday" option. */
const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

/**
 * The Recurrence object, its pattern, and IsRepeat — returned together so they cannot
 * drift apart.
 *
 * ── RecurrencePattern NAMES THE BLOCK TO READ ────────────────────────────────
 * This is the rule everything here follows, and it was confirmed the hard way. The
 * scheduler team put it plainly on 2026-09-17:
 *
 *   "If the RecurrencePattern is Daily then the time information should be provided
 *    within the Daily object, not in the Custom object."
 *   "If the Recurrence pattern = Custom only then you need to update Custom object.
 *    Otherwise you can leave it as empty."
 *
 * So the pattern and the filled block must always name the same thing. For a fortnight
 * this file sent `RecurrencePattern: "Daily"` with the time in `Custom` — a reading of
 * their 2026-09-11 message that turned out to be wrong. The scheduler looked in the Daily
 * block, found StartAt and RunAt empty, and the schedule never fired. That is why the
 * test schedules produced no email.
 *
 * Only one block is ever sent. Their service fills in the other five with blanks, which
 * is why a GET response carries all of them.
 *
 * ── TWO SETS OF FIELD NAMES ──────────────────────────────────────────────────
 * The simple blocks are not spelled like Custom. Custom has RecurrenceInterval and
 * RepeatOnDays; Daily and Weekly have `Frequency` and `WeekDay`/`WeekDays`. Taken from a
 * real GET response rather than guessed - but see the note on the Weekly block below.
 */
export function buildRecurrence(form) {
  const runAt = to24Hour(form.recurrence?.time || form.time);
  const startAt = form.date || "";
  const repeat = form.repeat;

  /* "Does not Repeat" — a single run at the date and time on the page. Also the fallback
     for Custom with no rule saved, which is a half-finished form rather than a schedule. */
  if (repeat === "none" || !repeat || (repeat === "custom" && !form.recurrence)) {
    return {
      RecurrencePattern: "NotRepeat",
      Recurrence: { NotRepeat: { StartAt: startAt, RunAt: runAt } },
      IsRepeat: false,
    };
  }

  /* Frequency is the interval — 1 is "every day". The simple options do not offer an
     interval at all; anyone wanting every third day picks Custom. */
  if (repeat === "daily") {
    return {
      RecurrencePattern: "Daily",
      Recurrence: { Daily: { StartAt: startAt, RunAt: runAt, Frequency: 1 } },
      IsRepeat: true,
    };
  }

  /* Both weekly options land in the same block and differ only by their days: one chosen
     day, or Monday to Friday.

     ── WHY BOTH WeekDay AND WeekDays ──
     The block carries a singular `WeekDay` and a plural `WeekDays`, and a response full
     of empties does not say which one the scheduler reads. Both are set: for a single day
     they agree, so either reading works. Only the array can express Monday-to-Friday, so
     if it turns out only the singular is read, that option is the one to revisit. Asked
     the scheduler team on 2026-09-18. */
  if (repeat === "weekly" || repeat === "weekdays") {
    const days = repeat === "weekdays" ? WEEKDAY_NAMES : [dayName(form.weeklyDay)].filter(Boolean);

    return {
      RecurrencePattern: "Weekly",
      Recurrence: {
        Weekly: {
          StartAt: startAt,
          RunAt: runAt,
          Frequency: 1,
          WeekDay: days.length === 1 ? days[0] : "",
          WeekDays: days,
        },
      },
      IsRepeat: true,
    };
  }

  const rec = form.recurrence;
  const custom = {
    RecurrenceType: "Daily",
    RecurrenceInterval: 1,
    StartAt: startAt,
    RunAt: runAt,
    RepeatOnDays: [],
    MonthlyOnDays: { Day: "", Week: "" },
    Ends: buildEnds(form.range),
  };

  if (rec.mode === "daily") {
    if (rec.daily?.type === "weekday") {
      /* "Every weekday" has no daily equivalent, but Mon-Fri every week says
         exactly the same thing. */
      custom.RecurrenceType = "Weekly";
      custom.RecurrenceInterval = 1;
      custom.RepeatOnDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
    } else {
      custom.RecurrenceType = "Daily";
      custom.RecurrenceInterval = Number(rec.daily?.interval) || 1;
    }
  } else if (rec.mode === "weekly") {
    custom.RecurrenceType = "Weekly";
    custom.RecurrenceInterval = Number(rec.weekly?.interval) || 1;
    custom.RepeatOnDays = (rec.weekly?.days || []).map(dayName);
  } else if (rec.mode === "monthly") {
    const monthly = rec.monthly || {};
    const isDayMode = monthly.type === "day";
    custom.RecurrenceType = "Monthly";
    custom.RecurrenceInterval = Number(isDayMode ? monthly.interval : monthly.theInterval) || 1;
    /* MonthlyOnDays.Day does double duty, confirmed against the dev service:
       day-of-month when Week is empty, weekday name when Week holds the
       ordinal. */
    custom.MonthlyOnDays = isDayMode
      ? { Day: String(Number(monthly.day) || 1), Week: "" }
      : { Day: dayName(monthly.weekday), Week: titleCase(monthly.ordinal) };
  } else if (rec.mode === "yearly") {
    const yearly = rec.yearly || {};
    const isOnMode = yearly.type === "on";
    custom.RecurrenceType = "Yearly";
    custom.RecurrenceInterval = 1;
    /* Custom has no Month field, so StartAt carries it — "every 1 year from
       15 June" is "15 June every year". */
    custom.StartAt = startOfTargetMonth(startAt, isOnMode ? yearly.month : yearly.theMonth, isOnMode ? yearly.day : 1);
    custom.MonthlyOnDays = isOnMode
      ? { Day: "", Week: "" }
      : { Day: dayName(yearly.weekday), Week: titleCase(yearly.ordinal) };
  }

  /* ── BACK TO "Custom", AS OF 2026-09-18 ──
     Between 2026-09-11 and 2026-09-18 this sent the cadence here instead — a weekly
     custom schedule went out as RecurrencePattern "Weekly" — on a reading of the
     scheduler team's message that turned out to be wrong. It cost us two silent
     failures: the pattern named a block that was empty, so nothing ran. See the rule at
     the top of this function.

     The cadence still exists, inside the block, as Custom.RecurrenceType. What changed
     back is only the pattern, which names where to look.

     Note the read side stays tolerant of both spellings on purpose - every schedule
     saved during that week is still in the database saying "Daily" or "Weekly" with a
     Custom block. See isCustomSchedule in scheduleMappers.js. */
  return {
    RecurrencePattern: "Custom",
    // RecurrencePattern: custom.RecurrenceType,   <- 2026-09-11 to 2026-09-18, broken
    Recurrence: { Custom: custom },
    IsRepeat: true,
  };
}

/* Range of Recurrence, which sits on the page rather than in the dialog. Only
   meaningful for Custom — a single run has nothing to end. */
function buildEnds(range) {
  const type = range?.type;
  return {
    IsEndsNever: type === "none",
    EndAt: type === "endBy" ? range.endBy || "" : "",
    EndAfterOccurrences: type === "after" ? Number(range.after) || 0 : 0,
  };
}

/**
 * The "what should the scheduler call" fields, read off the report's own
 * resource. Everything needed is already stored there by the resource form:
 * connectionData.baseUrl, .name and .requestValues.
 *
 * Reports carry exactly one resource — dashboards are the ones that can hold
 * several — so resources[0] is the resource rather than a guess. The guard
 * keeps that assumption honest instead of silently truncating.
 *
 * Needs a board from /dashboard-details/:id, which inlines the full resource
 * documents. The plain /dashboard list does not.
 */
export function apiFieldsFromBoard(board, reportId) {
  const resources = board?.resources || [];
  if (resources.length !== 1) {
    throw new Error(
      `Only single-resource reports can be scheduled (this one has ${resources.length}). ` +
        "Carrying more than one would need a payload that can hold several API calls.",
    );
  }

  /* The outer connectionData is the resource document, the inner one is its
     connection config — the same double hop the report generator makes. */
  const conn = resources[0].connectionData?.connectionData || {};
  const requestValues = conn.requestValues || {};

  /* baseUrl is stored as one complete URL including the path (the generator
     fetches it with nothing appended), so split it back apart here.

     Unused while the scheduler calls our backend instead of the data source - kept, with
     the lint rule silenced, so reverting is a matter of uncommenting the three lines
     below rather than rewriting this. */
  // eslint-disable-next-line no-unused-vars
  let origin = "";
  // eslint-disable-next-line no-unused-vars
  let route = "";
  try {
    const parsed = new URL(conn.baseUrl);
    origin = parsed.origin;
    route = parsed.pathname + parsed.search;
  } catch {
    // Not a parseable URL — send it whole rather than losing it entirely.
    origin = conn.baseUrl || "";
  }

  return {
    IsAPIUsed: true,
    APIName: conn.name || resources[0].connectionData?.name || "",

    /* ── THE SCHEDULER NOW CALLS OUR BACKEND, NOT THE DATA SOURCE ──
       Confirmed by the scheduler team on 2026-09-11: "In the request body there is
       BaseURL... Since scheduler is using your reporting backend API to generate the
       file. So in the request body, the BaseURL field should be
       https://api-reportv1.throbox.com".

       These three used to describe the report's own resource - where its rows come
       from. They now describe POST /generate-report on our reporting backend, which
       reads that resource itself and returns a rendered file.

       The previous values are kept commented rather than deleted: the design has moved
       twice already, and going back is a matter of swapping these lines. `origin`,
       `route` and requestValues.httpMethod above are still computed for that reason. */
    BaseURL: REPORT_API_BASE_URL,
    APIRoute: REPORT_API_ROUTE,
    APIMethod: "POST",

    // BaseURL: origin,
    // APIRoute: route,
    // APIMethod: String(requestValues.httpMethod || "POST").toUpperCase(),
    /* Stored as [{ key, value }] by the resource form; the payload wants a
       flat object. Note these values are whatever was typed when the resource
       was set up — if they include a fixed date, every run sends that same
       window. Raised with backend.

       ── WHY ReportID IS IN HERE AS WELL AS AT THE TOP LEVEL ──
       They are two different things and only one of them travels. The
       top-level ReportID is the scheduler's own bookkeeping and never leaves
       it. APIReqParams is the body the scheduler sends to whatever BaseURL
       names — so a service on the other end only ever sees a report id if it
       is in here.

       That is what the scheduler team asked for: the receiving service reads
       the report id to know it should return a rendered file rather than raw
       rows. Added last so it cannot be shadowed by a resource parameter that
       happens to share the name. */
    APIReqParams: {
      ...(requestValues.bodyParams || []).reduce((params, pair) => {
        if (pair?.key) params[pair.key] = pair.value;
        return params;
      }, {}),
      ...(reportId ? { ReportID: reportId } : {}),
    },
    ResponseFormat: "File",
  };
}

/**
 * Org and identity for the audit fields.
 *
 * Not from the redux user: `state.user.user` only holds what the login form put
 * there — username, tokens, permissions — so orgID and UserID are absent and
 * the payload went out with empty CreatedBy / OrganizationID. These come from
 * localStorage, which is where login writes them and where the rest of the app
 * reads them from (see DashboardView's fullUserDetails use).
 *
 * The one impure function here; buildSchedulePayload itself stays pure so the
 * payload can still be built from a plain object in a test.
 */
export function userFromStorage() {
  let details = {};
  try {
    details = JSON.parse(localStorage.getItem("fullUserDetails") || "{}");
  } catch {
    details = {};
  }

  return {
    id: localStorage.getItem("userID") || details.UserID || "",
    name: [details.FirstName, details.LastName].filter(Boolean).join(" "),
    orgID: localStorage.getItem("orgID") || "",
  };
}

const BLANK_API_FIELDS = {
  IsAPIUsed: true,
  APIName: "",
  BaseURL: "",
  APIRoute: "",
  APIMethod: "POST",
  APIReqParams: {},
  ResponseFormat: "File",
};

export function buildSchedulePayload({
  form,
  board = null,
  user = {},
  status = "Published",
  meta = {},
  now = new Date(),
}) {
  const { RecurrencePattern, Recurrence, IsRepeat } = buildRecurrence(form);
  /* form.reportId goes in so it lands inside APIReqParams too - see the note there. */
  const apiFields = board ? apiFieldsFromBoard(board, form.reportId) : BLANK_API_FIELDS;

  const userName = user.name || user.fullName || "";
  const userId = user.id || user.userID || "";
  const timestamp = now.toISOString();

  /* Field order follows the API doc so the two can be read side by side. */
  return {
    OrganizationID: user.orgID || user.organizationID || "",

    ReportID: form.reportId || "",
    ReportName: board?.name || meta.reportName || "",
    RecurrencePattern,

    ...apiFields,

    /* ── NOW A FIXED STRING ──
       "Please use WitmegReport as value for service name in the request" - scheduler
       team, 2026-09-11. ServiceName names the service the scheduler is calling, and it
       now calls our reporting backend rather than the report's data source.

       It used to be the report's own application name from the registry, matching the
       doc's "WebNeutriPOS" / "WebNeurolage" examples. Kept commented in case the flow
       moves back; meta.serviceName is still resolved by the page for the same reason. */
    ServiceName: REPORT_SERVICE_NAME,

    // ServiceName: meta.serviceName || "",

    EmailID: (form.recipients || []).map((r) => r.email).filter(Boolean),

    Recurrence,
    /* True for Custom, false for "Does not Repeat" — derived alongside
       RecurrencePattern so the two always agree. The dev service stores false
       either way at the moment; sending the right value regardless so it is
       correct as soon as they fix it. */
    IsRepeat,
    Type: "Report",

    // The Region value from GET /v1/scheduler/countries/timezone, e.g.
    // "Asia/Colombo".
    TimeZone: form.timeZone || "",

    TaskType: form.scheduleType || "Email",
    ResponseFormat: "File",

    ValidFrom: form.date || "",
    // Marked Required in the doc, but there is nothing to send for "after N
    // runs" or "no end date" — ScheduleEndCondition already says which it is.
    ValidTo: form.range?.type === "endBy" ? form.range.endBy || "" : "",
    ExecutionLimit: form.range?.type === "after" ? Number(form.range.after) || 0 : 0,
    ScheduleEndCondition: END_CONDITION[form.range?.type] ?? END_CONDITION.none,

    Subject: form.subject || "",
    Message: form.message || "",
    FileType: String(form.fileType || "PDF").toLowerCase(),

    /* VerticalName is gone. It came from a hardcoded verticals.json — a list of
       made-up ids that matched nothing in the app registry — and the select
       that filled it has been removed from the page. Worth confirming with the
       scheduler team that they no longer expect the field; nothing in the UI
       can supply it now. */

    /* The report's own application, chosen when the report was created.
       Now the same value the Application select holds, since the report list is
       filtered by it — but still read from the report so the two cannot disagree.

       From the board's top-level appList, not configDetails.appList: the schema stores
       it beside configDetails rather than inside it, and reading the nested path sent
       an empty ApplicationID on every schedule ever created. */
    ApplicationID: appListOf(board)[0] || "",

    // Sits separately from CreatedBy in the doc, so it carries the publisher —
    // who the report goes out as — while CreatedBy is whoever is signed in.
    ScheduleCreatedBy: meta.publisherName || "",

    CreatedDate: timestamp,
    CreatedBy: userName,
    CreatedUserID: userId,
    ModifiedDate: timestamp,
    ModifiedBy: userName,
    ModifiedUserID: userId,

    /* ── OBJECTS, NOT STRINGS ──
       The 17-07-2023 doc says `[] string`, and this did send bare numbers on that basis.
       The stored records disagree: a schedule created before this app existed holds
       `[{ Name: "Mr Tha Siva", PhoneNumber: "+94769287848" }]`. Following the data over
       the doc, since the data is what the service actually wrote — flagged with the
       scheduler team.

       Filled only for a WhatsApp or SMS schedule; an email schedule's recipients carry no
       number, so this comes out empty on its own without needing to check TaskType. */
    PhoneNumbers: (form.recipients || [])
      .filter((r) => r.phone)
      .map((r) => ({ Name: r.name || "", PhoneNumber: r.phone })),

    /* ── DRAFT OR PUBLISHED ──
       Status is the field the scheduler added for this, and the one that counts:
       "Draft" when the user hits Keep as Draft, "Published" when they hit
       Publish. One record either way — they were explicit about not keeping
       drafts in a separate table.

       Leading capital since 2026-09-17 — see STATUS at the top of this file. */
    Status: status === "Draft" ? STATUS.draft : STATUS.published,

    /* Must be sent explicitly: leave it out and the schedule saves with
       IsActive false and never fires. Kept alongside Status because it is what
       actually stops a schedule running — Status says what a schedule is, while
       IsActive says whether it fires. A draft is both: not published, and not
       running. */
    IsActive: status !== "Draft",

    /* Superseded by Status, and kept only because older records carry it. It was
       sent on create and never came back from either GET, so it never worked;
       Status is what to read now. Safe to drop once no schedule created before
       Status existed still matters. */
    IsDraft: status === "Draft",
  };
}

export default buildSchedulePayload;
